import { FGCustomPanel, FGSelectInput } from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import {
  DogApi,
  EDogType,
  ERequestState,
  PedigreeDuplicataRequestApi,
} from "../../../../api";
import { useAuth } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { DrupalStepperButtonBloc } from "../../../common";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";

export interface IDrupalDuplicataFormProps {
  pedigreeDuplicataRequestId: number;
  stepNumber: number;
  initialdata: any;
  onStepChange: (newStep: number) => void;
  save: (data?: any) => Promise<any> | any;
  loading: boolean;
}

export const DrupalDuplicataForm: React.FunctionComponent<IDrupalDuplicataFormProps> = ({
  onStepChange,
  pedigreeDuplicataRequestId,
  stepNumber,
  save,
  loading,
  initialdata,
}) => {
  const { user } = useAuth();
  const { t } = useTl();
  const dogApi = useApiService(DogApi);
  const api = useApiService(PedigreeDuplicataRequestApi);
  const [currentStep, setCurrentStep] = React.useState(stepNumber);

  const saveForm = React.useCallback(
    async (formikData) => {
      if (currentStep === 1) {
        await save(formikData);
      }
    },
    [currentStep, save]
  );

  const [dogs, dloading] = useReferential(
    (a) =>
      a.getOwnedDogs(
        user.userId,
        initialdata?.dogId ? initialdata.dogId : 0,
        null,
        null,
        null
      ),
    true,
    []
  );

  const formSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test(
          "dog-authorized",
          t(ETLCodes.DogIsNotBelgianWithPedigree),
          async function (value) {
            const type = await dogApi.getDogType(value);
            return type === EDogType.BelgianDogWithPedigree;
          }
        )
        .test(
          "dog-availability",
          t(ETLCodes.DogHasAnotherRequestError),
          async function (value) {
            const res = await api.isDogAvailable(
              value,
              pedigreeDuplicataRequestId
            );
            return res.value;
          }
        )
        .test(
          "is-not-excluded",
          t(ETLCodes.DogIsExcluded),
          async function (value) {
            if (pedigreeDuplicataRequestId <= 0) {
              const result = await dogApi.isDogExcluded(value);
              return !result.value;
            }
            return true;
          }
        ),
    });
  }, [api, dogApi, pedigreeDuplicataRequestId, t]);

  return (
    <DrupalFormGenerator
      initialValues={initialdata}
      editMode={
        currentStep === 0 &&
        initialdata.request.status === ERequestState.REQUESTED
      }
      onSubmit={() => {}}
      saving={false}
      validationSchema={formSchema}
      loading={loading}
      hideButtons
    >
      {currentStep !== 0 ? (
        <DrupalFieldSet title={t(ETLCodes.RequestResume)} />
      ) : (
        <></>
      )}
      <FGSelectInput
        name="dogId"
        label={t(ETLCodes.Dog)}
        items={dogs}
        loading={dloading}
        readonly={initialdata.pedigreeDuplicataRequestId}
      />
      {pedigreeDuplicataRequestId <= 1 ||
      initialdata?.request?.status === ERequestState.REQUESTED ? (
        <FGCustomPanel>
          {(ctx) => {
            return (
              <DrupalStepperButtonBloc
                backOnly={
                  initialdata?.request?.status !== ERequestState.REQUESTED
                }
                disabled={
                  ctx.formik.values === initialdata || !ctx.formik.isValid
                }
                loading={loading}
                onStepChange={(newStep) => {
                  setCurrentStep(newStep);
                  onStepChange(newStep);
                }}
                stepNumber={currentStep}
                onNextFn={() => saveForm(ctx.formik.values)}
                formik={ctx.formik}
              />
            );
          }}
        </FGCustomPanel>
      ) : null}
    </DrupalFormGenerator>
  );
};
